/**
 * หลังบ้านพาร์ทเนอร์ (/affiliate, owner + head) — pure logic, unit-tested in test/affiliate.spec.ts:
 * tabs + who may open the page · labels · how long an application has waited · tier steps checked like the affiliate
 * server checks them (instant feedback; the server still decides) · what changed in a template · a pin from a Google
 * Maps link · the Idempotency-Key of a write · P3: a campaign's one-line summary and its rules (as the server's) ·
 * P4: money in satang ("฿1,862.00"), months ("ต.ค. 2569"), a payout's documents chip, a claim amount typed in baht ·
 * P5: a gym's pickup ("รอบรวม กทม. ทุกวัน (Lalamove)" / "กล่องรวม DHL ทุก จ. และ พฤ."), its Lalamove zone line (the
 * stored answer, R5, or this visit's check), the DHL address of its box (prefilled from the gym's address text, checked
 * like the server), order modes.
 */
import type {
  AdminDoc,
  AdminPayoutRow,
  BoxAddress,
  Campaign,
  CampaignIn,
  DocKind,
  DocNeed,
  Entity,
  PartnerStatus,
  PartnerType,
  PickupKind,
  PlaceCheck,
  Sla,
  StoredZone,
  Step,
  Template,
} from '../api/affiliate/types'
import type { StaffRole } from '../api/session'
import type { IconName } from '../components/ui/icons'

// ---------------------------------------------------------------- page + who

export type AffTabId = 'overview' | 'apply' | 'partners' | 'tiers' | 'payout'

/** `pending` = the tab shows how many wait for the team (ใบสมัคร: applications · ถอนเงิน: open withdrawal requests) */
export const AFF_TABS: { id: AffTabId; label: string; icon: IconName; pending?: boolean }[] = [
  { id: 'overview', label: 'ภาพรวม', icon: 'layoutGrid' },
  { id: 'apply', label: 'ใบสมัคร', icon: 'userPlus', pending: true },
  { id: 'partners', label: 'พาร์ทเนอร์', icon: 'users' },
  { id: 'tiers', label: 'แม่แบบและแคมเปญ', icon: 'layers' },
  { id: 'payout', label: 'ถอนเงิน', icon: 'wallet', pending: true },
]

export function affTabOf(v: unknown): AffTabId {
  return AFF_TABS.some((t) => t.id === v) ? (v as AffTabId) : 'overview'
}

/** The owner and the heads; never the Meta reviewer (the server answers 403 to anyone else). */
export function canSeeAffiliate(role: StaffRole | null | undefined, reviewer = false): boolean {
  return (role === 'owner' || role === 'head') && !reviewer
}

/** App.vue's guard: /affiliate for managers only (any other page is not this rule's business). */
export function affiliatePageAllowed(name: string, me: { role: StaffRole; reviewer?: boolean } | null | undefined): boolean {
  if (!me || name !== 'affiliate') return true
  return canSeeAffiliate(me.role, !!me.reviewer)
}

// ---------------------------------------------------------------- labels

export const TYPE_LABEL: Record<PartnerType, string> = { gym: 'ฟิตเนส', trainer: 'เทรนเนอร์', nutritionist: 'นักโภชนาการ' }

export const STATUS_LABEL: Record<PartnerStatus, string> = {
  applied: 'รออนุมัติ',
  approved: 'อนุมัติแล้ว',
  rejected: 'ไม่อนุมัติ',
  suspended: 'ระงับ',
}

/** the list row's colour: amber when under 24 h is left of the 2 business days, red once past */
export function slaClass(sla: Sla): '' | 'sla-soon' | 'sla-late' {
  return sla === 'soon' ? 'sla-soon' : sla === 'late' ? 'sla-late' : ''
}

/** "1 วัน" · "5 ชม." · "20 นาที" — how long since `iso` (whole units, rounded down) */
export function waitedText(iso: string, now: number): string {
  const ms = Math.max(0, now - Date.parse(iso))
  const min = Math.floor(ms / 60_000)
  if (min < 1) return 'ไม่ถึงนาที'
  if (min < 60) return `${min} นาที`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h} ชม.`
  return `${Math.floor(h / 24)} วัน`
}

const BKK_MS = 7 * 3600_000
const TH_MONTH = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

/** "วันนี้ 20:42" · "เมื่อวาน 19:05" · "25 ต.ค." (Bangkok time) */
export function whenText(iso: string | null | undefined, now: number): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return ''
  const d = new Date(t + BKK_MS)
  const day = Math.floor((t + BKK_MS) / 86_400_000)
  const today = Math.floor((now + BKK_MS) / 86_400_000)
  const hm = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
  if (day === today) return `วันนี้ ${hm}`
  if (day === today - 1) return `เมื่อวาน ${hm}`
  return `${d.getUTCDate()} ${TH_MONTH[d.getUTCMonth()]}`
}

/** a due time with its hour (review M8): "วันนี้ 22:30" · "พรุ่งนี้ 10:00" · "28 ต.ค. 10:00" (Bangkok time) */
export function dueText(iso: string | null | undefined, now: number): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return ''
  const d = new Date(t + BKK_MS)
  const day = Math.floor((t + BKK_MS) / 86_400_000)
  const today = Math.floor((now + BKK_MS) / 86_400_000)
  const hm = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
  if (day === today) return `วันนี้ ${hm}`
  if (day === today + 1) return `พรุ่งนี้ ${hm}`
  return `${d.getUTCDate()} ${TH_MONTH[d.getUTCMonth()]} ${hm}`
}

/** "1 พ.ย. 2569" from "2026-11-01" (a tier set's effective_from) */
export function dateText(ymd: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd ?? '')
  if (!m) return ''
  return `${Number(m[3])} ${TH_MONTH[Number(m[2]) - 1]} ${Number(m[1]) + 543}`
}

// ---------------------------------------------------------------- tiers

export const MAX_STEPS = 6
export const PCT_MIN = 5
export const PCT_MAX = 20
export const MAX_BAHT = 10_000_000
export const OVERRIDE_MAX = 5

/** whole baht with its sign: "฿42,910" · "−฿1,234" (a month with a late cancellation can be negative: review M6) */
export const baht = (n: number): string => {
  const r = Math.round(n)
  return `${r < 0 ? '−' : ''}฿${Math.abs(r).toLocaleString('en-US')}`
}
export const pctText = (p: number): string => `${Number.isInteger(p) ? p : p.toFixed(1)}%`

const halfStep = (p: number): boolean => Number.isFinite(p) && Math.abs(p * 2 - Math.round(p * 2)) < 1e-9

/** null = fine; else what is wrong, in Thai (the same rules as the affiliate server) */
export function validateSteps(steps: Step[]): string | null {
  if (steps.length < 1) return 'ต้องมีอย่างน้อย 1 ขั้น'
  if (steps.length > MAX_STEPS) return `ได้ไม่เกิน ${MAX_STEPS} ขั้น`
  if (steps[0]!.min_baht !== 0) return 'ขั้นแรกต้องเริ่มที่ ฿0'
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i]!
    if (!Number.isFinite(s.pct) || s.pct < PCT_MIN || s.pct > PCT_MAX) return `% ต้องอยู่ระหว่าง ${PCT_MIN} ถึง ${PCT_MAX}`
    if (!halfStep(s.pct)) return '% ต้องเพิ่มทีละ 0.5'
    if (!Number.isInteger(s.min_baht) || s.min_baht < 0) return 'ยอดต้องเป็นจำนวนเต็มบาท'
    if (s.min_baht > MAX_BAHT) return `ยอดสูงสุด ${baht(MAX_BAHT)}`
    if (i > 0) {
      const prev = steps[i - 1]!
      if (s.min_baht <= prev.min_baht) return 'ยอดต้องเพิ่มขึ้นทุกขั้น'
      if (s.pct <= prev.pct) return '% ต้องเพิ่มขึ้นทุกขั้น'
    }
  }
  return null
}

export function stepsEqual(a: Step[], b: Step[]): boolean {
  return a.length === b.length && a.every((s, i) => s.min_baht === b[i]!.min_baht && s.pct === b[i]!.pct)
}

const minText = (n: number): string => (n === 0 ? 'เริ่มต้น' : baht(n))

/** "5% เริ่มต้น · 10% ฿10,000 · …" — a template's own steps (templates may use different percentages) */
export function stepsText(steps: Step[]): string {
  return steps.map((s) => `${pctText(s.pct)} ${minText(s.min_baht)}`).join(' · ')
}

/** "5–20%" (or "10%" for one step) */
export function rangeText(r: { min_pct: number; max_pct: number } | null | undefined): string {
  if (!r) return '—'
  return r.min_pct === r.max_pct ? pctText(r.min_pct) : `${pctText(r.min_pct).replace('%', '')}–${pctText(r.max_pct)}`
}

/** The first difference between two step lists, in words ("ขั้น 10% จาก ฿10,000 เป็น ฿15,000"); '' when the same. */
export function describeStepChange(before: Step[], after: Step[]): string {
  const n = Math.max(before.length, after.length)
  for (let i = 0; i < n; i++) {
    const b = before[i]
    const a = after[i]
    if (b && a && b.min_baht === a.min_baht && b.pct === a.pct) continue
    if (!b && a) return `เพิ่มขั้น ${pctText(a.pct)} ที่ ${baht(a.min_baht)}`
    if (b && !a) return `เอาขั้น ${pctText(b.pct)} ที่ ${baht(b.min_baht)} ออก`
    if (b && a) {
      if (b.pct === a.pct) return `ขั้น ${pctText(a.pct)} จาก ${minText(b.min_baht)} เป็น ${minText(a.min_baht)}`
      if (b.min_baht === a.min_baht) return `ขั้น ${minText(a.min_baht)} จาก ${pctText(b.pct)} เป็น ${pctText(a.pct)}`
      return `ขั้นที่ ${i + 1} จาก ${pctText(b.pct)} ที่ ${baht(b.min_baht)} เป็น ${pctText(a.pct)} ที่ ${baht(a.min_baht)}`
    }
  }
  return ''
}

/** the template a new partner of this type starts on (its type's default, else the first one), or 'custom' */
export function defaultTemplateId(templates: Template[], type: PartnerType): number | 'custom' {
  const live = templates.filter((t) => !t.archived)
  return (live.find((t) => t.default_for_type === type) ?? live[0])?.id ?? 'custom'
}

/** what a form sends for its tiers: the template while its steps are untouched, else the steps themselves */
export function tierChoice(tplId: number | 'custom', steps: Step[]): { template_id: number } | { steps: Step[] } {
  return tplId === 'custom' ? { steps: steps.map((s) => ({ ...s })) } : { template_id: tplId }
}

/** "instagram.com/x" → a safe https link (only http/https; anything else is shown as text) */
export function socialHref(s: string): string | null {
  const t = s.trim()
  if (!t) return null
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(t) ? t : `https://${t.replace(/^\/+/, '')}`
  try {
    const u = new URL(withScheme)
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null
  } catch {
    return null
  }
}

// ---------------------------------------------------------------- campaigns (P3)

const ymdOf = (t: number): string => new Date(t + BKK_MS).toISOString().slice(0, 10)
const YMD = /^(\d{4})-(\d{2})-(\d{2})$/

/** a real calendar date "YYYY-MM-DD" (not 2026-13-01 / 2026-02-30) */
function isYmd(v: unknown): v is string {
  const m = typeof v === 'string' ? YMD.exec(v) : null
  if (!m) return false
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3])
}

/** "15 พ.ย." — with the short Buddhist year ("15 ม.ค. 70") when it is not this year */
function dayMonth(ymd: string, year: number): string {
  const m = YMD.exec(ymd)
  if (!m) return ymd
  const y = Number(m[1])
  return `${Number(m[3])} ${TH_MONTH[Number(m[2]) - 1]}${y === year ? '' : ` ${(y + 543) % 100}`}`
}

/** "ฟิตเนส" · "ฟิตเนส และเทรนเนอร์" · "ฟิตเนส เทรนเนอร์ และนักโภชนาการ" */
function typesText(types: PartnerType[]): string {
  const names = types.map((t) => TYPE_LABEL[t])
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(' ')} และ${names[names.length - 1]}`
}

/**
 * A campaign's line under its name (mockup 4.3): which shops · when · how often used, joined by " · ":
 * "ทุกร้าน · 1–30 พ.ย. · ใช้แล้ว 41 ครั้ง" / "เลือก 12 ร้าน · ถึง 31 ธ.ค." / "ร้านที่อนุมัติไม่เกิน 30 วัน" / "ฟิตเนส · เริ่ม 15 พ.ย.".
 * Dates (Bangkok "today"): not started → the range, or "เริ่ม …" without an end · running → "ถึง …" (nothing when it has no
 * end) · over → "จบแล้ว …". Uses only once there are some.
 */
export function campaignSub(c: Campaign, now: number = Date.now()): string {
  const parts: string[] = []
  if (c.scope === 'all') parts.push('ทุกร้าน')
  else if (c.scope === 'types') parts.push(typesText(c.scope_types ?? []))
  else if (c.scope === 'partners') parts.push(`เลือก ${(c.scope_partners ?? []).length} ร้าน`)
  else parts.push(`ร้านที่อนุมัติไม่เกิน ${c.scope_new_days ?? 0} วัน`)
  const today = ymdOf(now)
  const year = Number(today.slice(0, 4))
  const start = c.starts_on
  const end = c.ends_on
  if (end && end < today) parts.push(`จบแล้ว ${dayMonth(end, year)}`)
  else if (start > today) {
    if (!end) parts.push(`เริ่ม ${dayMonth(start, year)}`)
    else if (start.slice(0, 7) === end.slice(0, 7) && Number(start.slice(0, 4)) === year) {
      parts.push(`${Number(start.slice(8))}–${dayMonth(end, year)}`)
    } else parts.push(`${dayMonth(start, year)} – ${dayMonth(end, year)}`)
  } else if (end) parts.push(`ถึง ${dayMonth(end, year)}`)
  if (c.uses > 0) parts.push(`ใช้แล้ว ${c.uses.toLocaleString('en-US')} ครั้ง`)
  return parts.filter(Boolean).join(' · ')
}

/** a campaign as PUT sends it back (the counters dropped, partner refs → ids) */
export function campaignToIn(c: Campaign): CampaignIn {
  return {
    name: c.name, line_label: c.line_label, kind: c.kind, value: c.value, max_baht: c.max_baht,
    min_order_baht: c.min_order_baht, audience: c.audience, once_per_customer: c.once_per_customer, scope: c.scope,
    scope_types: c.scope_types ? [...c.scope_types] : null,
    scope_partner_ids: c.scope_partners ? c.scope_partners.map((p) => p.id) : null,
    scope_new_days: c.scope_new_days, starts_on: c.starts_on, ends_on: c.ends_on, active: c.active,
  }
}

export type CampaignField = keyof CampaignIn
export const CAMPAIGN_NAME_MAX = 80
export const CAMPAIGN_LABEL_MAX = 60
export const CAMPAIGN_PCT_MAX = 50
export const CAMPAIGN_BAHT_MAX = 5000
export const CAMPAIGN_MIN_ORDER_MAX = 1_000_000
export const CAMPAIGN_NEW_DAYS_MAX = 365
const PARTNER_TYPES: PartnerType[] = ['gym', 'trainer', 'nutritionist']

/** what to say under a field the form or the server (422 invalid `fields`) refused */
export function campaignFieldText(field: string, c: Pick<CampaignIn, 'kind'>): string {
  switch (field) {
    case 'name': return `ใส่ชื่อแคมเปญ ไม่เกิน ${CAMPAIGN_NAME_MAX} ตัวอักษร`
    case 'line_label': return `ใส่ข้อความที่ลูกค้าเห็น ไม่เกิน ${CAMPAIGN_LABEL_MAX} ตัวอักษร`
    case 'kind': return 'เลือกลดเป็นบาทหรือเป็น %'
    case 'value':
      return c.kind === 'pct' ? `ลดได้ 0.5% ถึง ${CAMPAIGN_PCT_MAX}% ทีละ 0.5` : `ลดได้ ฿1 ถึง ${baht(CAMPAIGN_BAHT_MAX)} (จำนวนเต็มบาท)`
    case 'max_baht': return `ลดสูงสุด ฿1 ถึง ${baht(CAMPAIGN_BAHT_MAX)} หรือเว้นว่างไว้`
    case 'min_order_baht': return `ยอดขั้นต่ำเป็นจำนวนเต็ม ฿0 ถึง ${baht(CAMPAIGN_MIN_ORDER_MAX)}`
    case 'audience': return 'เลือกกลุ่มลูกค้า'
    case 'scope': return 'เลือกร้านที่ใช้ได้'
    case 'scope_types': return 'เลือกอย่างน้อย 1 ประเภท'
    case 'scope_partner_ids': return 'เลือกอย่างน้อย 1 ร้าน (เฉพาะร้านที่อนุมัติแล้ว)'
    case 'scope_new_days': return `ใส่จำนวนวัน 1 ถึง ${CAMPAIGN_NEW_DAYS_MAX}`
    case 'starts_on': return 'เลือกวันเริ่ม'
    case 'ends_on': return 'วันสิ้นสุดต้องไม่ก่อนวันเริ่ม'
    default: return 'ข้อมูลช่องนี้ไม่ถูกต้อง'
  }
}

const wholeIn = (v: unknown, lo: number, hi: number): boolean =>
  typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi

/**
 * The affiliate server's campaign rules (admin_campaigns._validate), for instant feedback: every failing field with
 * its words ({} = fine). The server still decides; its 422 `fields` use the same names.
 */
export function validateCampaign(c: CampaignIn): Partial<Record<CampaignField, string>> {
  const bad: CampaignField[] = []
  const name = typeof c.name === 'string' ? c.name.split(/\s+/).filter(Boolean).join(' ') : ''
  if ([...name].length < 1 || [...name].length > CAMPAIGN_NAME_MAX) bad.push('name')
  const label = typeof c.line_label === 'string' ? c.line_label.trim() : ''
  if ([...label].length < 1 || [...label].length > CAMPAIGN_LABEL_MAX) bad.push('line_label')
  if (c.kind !== 'baht' && c.kind !== 'pct') bad.push('kind')
  else if (c.kind === 'pct' ? !(Number.isFinite(c.value) && c.value >= 0.5 && c.value <= CAMPAIGN_PCT_MAX && halfStep(c.value))
    : !wholeIn(c.value, 1, CAMPAIGN_BAHT_MAX)) bad.push('value')
  if (c.max_baht !== null && !wholeIn(c.max_baht, 1, CAMPAIGN_BAHT_MAX)) bad.push('max_baht')
  if (!wholeIn(c.min_order_baht, 0, CAMPAIGN_MIN_ORDER_MAX)) bad.push('min_order_baht')
  if (c.audience !== 'all' && c.audience !== 'new_customer') bad.push('audience')
  if (typeof c.once_per_customer !== 'boolean') bad.push('once_per_customer')
  if (c.scope === 'types') {
    const t = c.scope_types
    if (!Array.isArray(t) || !t.length || t.some((x) => !PARTNER_TYPES.includes(x))) bad.push('scope_types')
  } else if (c.scope === 'partners') {
    const ids = c.scope_partner_ids
    if (!Array.isArray(ids) || !ids.length || ids.length > 500 || ids.some((i) => !Number.isInteger(i))) bad.push('scope_partner_ids')
  } else if (c.scope === 'new_partners') {
    if (!wholeIn(c.scope_new_days, 1, CAMPAIGN_NEW_DAYS_MAX)) bad.push('scope_new_days')
  } else if (c.scope !== 'all') bad.push('scope')
  if (!isYmd(c.starts_on)) bad.push('starts_on')
  if (c.ends_on !== null && (!isYmd(c.ends_on) || (isYmd(c.starts_on) && c.ends_on < c.starts_on))) bad.push('ends_on')
  if (typeof c.active !== 'boolean') bad.push('active')
  const out: Partial<Record<CampaignField, string>> = {}
  for (const f of bad) out[f] = campaignFieldText(f, c)
  return out
}

// ---------------------------------------------------------------- money and payouts (P4)

/** the digits of satang as baht with 2 decimals, no sign: "1,862.00" (integer arithmetic, never a float) */
export function satangNum(n: number): string {
  const abs = Math.abs(Math.trunc(n))
  return `${Math.floor(abs / 100).toLocaleString('en-US')}.${String(abs % 100).padStart(2, '0')}`
}

/** "฿1,862.00" · "−฿55.86" */
export function satangText(n: number): string {
  return `${n < 0 ? '−' : ''}฿${satangNum(n)}`
}

/** whole baht from satang, half away from zero (the overview's money: "฿42,910") */
export function bahtFromSatang(n: number): number {
  const v = Math.trunc(n)
  return Math.sign(v) * Math.floor((Math.abs(v) + 50) / 100)
}

/** a withdrawal row's colour: amber under 24 h before its 3 business days are up, red once past (open requests only) */
export function payoutSlaClass(row: Pick<AdminPayoutRow, 'sla' | 'status'>): '' | 'sla-soon' | 'sla-late' {
  return row.status === 'requested' && row.sla ? slaClass(row.sla) : ''
}

/** "ต.ค. 2569" from "2026-10" */
export function monthLabel(ym: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ym ?? '')
  if (!m) return ''
  return `${TH_MONTH[Number(m[2]) - 1]} ${Number(m[1]) + 543}`
}

/** "ต.ค." from "2026-10" (a tile's label: "โอนแล้ว ต.ค.") */
export function monthShort(ym: string | null | undefined): string {
  const m = /^\d{4}-(\d{2})$/.exec(ym ?? '')
  return m ? TH_MONTH[Number(m[1]) - 1]! : ''
}

/** this Bangkok month, "YYYY-MM" */
export function bkkMonth(now: number): string {
  return new Date(now + BKK_MS).toISOString().slice(0, 7)
}

/** today in Bangkok, "YYYY-MM-DD" */
export function bkkToday(now: number): string {
  return new Date(now + BKK_MS).toISOString().slice(0, 10)
}

/** the Bangkok date of an instant, "YYYY-MM-DD" */
export function bkkDateOf(iso: string): string {
  return new Date(Date.parse(iso) + BKK_MS).toISOString().slice(0, 10)
}

/** this Bangkok month and the `n - 1` before it, newest first (a month picker) */
export function recentMonths(now: number, n = 12): string[] {
  const d = new Date(now + BKK_MS)
  const out: string[] = []
  for (let i = 0; i < n; i++) {
    const m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1))
    out.push(m.toISOString().slice(0, 7))
  }
  return out
}

export const DOC_KIND_LABEL: Record<DocKind, string> = {
  id_card: 'บัตรประชาชน',
  company_cert: 'หนังสือรับรองบริษัท',
  bank_book: 'หน้าบัญชี',
}

export const ENTITY_LABEL: Record<Entity, string> = { person: 'บุคคลธรรมดา', company: 'นิติบุคคล' }

/** what "ขอเอกสารใหม่" asks for, in the payee's words: the ID document of its entity, or the bank book */
export function docNeedLabel(need: DocNeed, entity: Entity): string {
  if (need === 'bank_book') return DOC_KIND_LABEL.bank_book
  return entity === 'company' ? DOC_KIND_LABEL.company_cert : DOC_KIND_LABEL.id_card
}

export interface Chip {
  text: string
  cls: 'tg-ok' | 'tg-warn' | 'tg-crit' | 'tg-n'
  title?: string
}

/** the affiliate takes at most this many rows in one mark-paid (422 invalid ["items"] above it) */
export const MARK_PAID_MAX = 50

/**
 * The queue's documents column (mockup 4.2): "ครบ" · "ไม่มีหน้าบัญชี" · "ไม่มีบัตร/หนังสือรับรอง" (both missing: the two
 * joined). The affiliate accepts a request only with both documents live, so a gap on a row means the team asked for a
 * new one and the partner has not uploaded it yet — said in the title ("รอเอกสารใหม่"; the drawer shows the note).
 * Ruling R10/R12: complete documents nobody on the team has confirmed yet (a first withdrawal, a new bank account or
 * payee, a re-upload) read "เอกสารรอตรวจ" — mark-paid refuses the row until the drawer's "ยืนยันเอกสารแล้ว".
 */
export function docsChip(
  row: Pick<AdminPayoutRow, 'docs_ok' | 'docs_missing'> & Partial<Pick<AdminPayoutRow, 'docs_review' | 'first_payout' | 'account_changed'>>,
): Chip {
  if (row.docs_ok && row.docs_review) {
    const why = row.account_changed ? 'บัญชีใหม่' : row.first_payout ? 'ถอนครั้งแรก' : 'มีเอกสารใหม่'
    return { text: 'เอกสารรอตรวจ', cls: 'tg-warn', title: `${why} ยืนยันเอกสารก่อนโอน` }
  }
  if (row.docs_ok) return { text: 'ครบ', cls: 'tg-ok' }
  const id = row.docs_missing.includes('id_doc')
  const bank = row.docs_missing.includes('bank_book')
  const text = id && bank ? 'ไม่มีบัตร/หนังสือรับรอง · หน้าบัญชี' : id ? 'ไม่มีบัตร/หนังสือรับรอง' : 'ไม่มีหน้าบัญชี'
  return { text, cls: 'tg-crit', title: 'รอเอกสารใหม่ โอนไม่ได้จนกว่าจะแนบ' }
}

/** one document's chip in the drawer: "ส่งแล้ว รอตรวจ" · "ตรวจแล้ว" · "รอเอกสารใหม่" (the note under it) */
export function docStatusChip(d: Pick<AdminDoc, 'status'>): Chip {
  if (d.status === 'accepted') return { text: 'ตรวจแล้ว', cls: 'tg-ok' }
  if (d.status === 'submitted') return { text: 'ส่งแล้ว รอตรวจ', cls: 'tg-n' }
  if (d.status === 'rejected') return { text: 'รอเอกสารใหม่', cls: 'tg-warn' }
  return { text: 'ฉบับเก่า', cls: 'tg-n' }
}

/** the IDs of the documents behind a payout that wait for the team (R10): its entity's ID document and the bank book,
 *  still `submitted` — what "ยืนยันเอกสารแล้ว" sends (the affiliate wants exactly these, else 409 docsChanged) */
export function waitingDocIds(docs: Pick<AdminDoc, 'id' | 'kind' | 'status'>[], entity: Entity): number[] {
  const kinds: DocKind[] = [entity === 'company' ? 'company_cert' : 'id_card', 'bank_book']
  return docs.filter((d) => d.status === 'submitted' && kinds.includes(d.kind)).map((d) => d.id).sort((a, b) => a - b)
}

/** a fetched file to the browser's downloads under the gateway's name (the month's 50 ทวิ) */
export function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/** "1-2345-67890-12-1" from 13 digits (anything else as it is) */
export function taxIdText(digits: string): string {
  const d = (digits ?? '').replace(/\D/g, '')
  if (d.length !== 13) return digits ?? ''
  return `${d[0]}-${d.slice(1, 5)}-${d.slice(5, 10)}-${d.slice(10, 12)}-${d[12]}`
}

/** a claim amount typed in baht ("42.7", "1,000", "฿12.50") → satang; null when it is not a positive amount with at
 *  most 2 decimals (no float: "0.29" is 29, not 28.999…) */
export function parseBahtToSatang(text: string): number | null {
  const t = (text ?? '').replace(/[฿,\s]/g, '')
  const m = /^(\d{1,9})(?:\.(\d{1,2}))?$/.exec(t)
  if (!m) return null
  const satang = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0') || '0')
  return satang > 0 ? satang : null
}

// ---------------------------------------------------------------- gym pickup, zone and box (P5)

/** the box weekdays the owner picks from (Mon–Fri; the affiliate stores 1..5) */
export const BOX_DAYS: { d: number; label: string }[] = [
  { d: 1, label: 'จ.' },
  { d: 2, label: 'อ.' },
  { d: 3, label: 'พ.' },
  { d: 4, label: 'พฤ.' },
  { d: 5, label: 'ศ.' },
]
const DAY_ABBR = new Map(BOX_DAYS.map((x) => [x.d, x.label]))

/** [1] "ทุก จ." · [1, 4] "ทุก จ. และ พฤ." · [1, 3, 5] "ทุก จ. พ. และ ศ." (the affiliate's box_days_text + "ทุก");
 *  nothing → "" */
export function boxDaysText(days: number[] | null | undefined): string {
  const words = [...new Set(days ?? [])].sort((a, b) => a - b).map((d) => DAY_ABBR.get(d)).filter((w): w is string => !!w)
  if (!words.length) return ''
  if (words.length === 1) return `ทุก ${words[0]}`
  return `ทุก ${words.slice(0, -1).join(' ')} และ ${words[words.length - 1]}`
}

export const PICKUP_LALAMOVE = 'รอบรวม กทม. ทุกวัน (Lalamove)'

/** "รับที่ฟิตเนสแบบ": the gym's pickup by the shop's rule (`pickup_kind`); null = customers cannot pick up there (or
 *  an affiliate before P5) */
export function pickupText(p: { pickup_kind?: PickupKind | null; box_days?: number[] | null }): string | null {
  if (p.pickup_kind === 'lalamove') return PICKUP_LALAMOVE
  if (p.pickup_kind === 'box') return `กล่องรวม DHL ${boxDaysText(p.box_days)}`.trim()
  return null
}

/** an out-of-zone gym with a freezer and no box days: customers cannot pick up there (review I-1, ruling R5) */
export const PICKUP_NEEDS_BOX = 'ยังรับที่ฟิตเนสไม่ได้ ตั้งวันส่งประจำก่อน'
export const PICKUP_NEEDS_FREEZER = 'ยังรับที่ฟิตเนสไม่ได้ (ไม่มีช่องแช่แข็ง)'

/** the "รับที่ฟิตเนสแบบ" row of an approved gym: a box is a box; outside Lalamove without one nobody picks up there
 *  (whatever an affiliate before R5 still says: P1 refuses the round), so the row says what is missing; else the
 *  server's pickup, or why there is none */
export function pickupRowText(
  p: { pickup_kind?: PickupKind | null; box_days?: number[] | null; has_freezer: boolean | null },
  inZone: boolean | null,
): string {
  if (p.pickup_kind === 'box') return pickupText(p)!
  if (inZone === false) return p.has_freezer ? PICKUP_NEEDS_BOX : PICKUP_NEEDS_FREEZER
  if (p.pickup_kind === 'lalamove') return PICKUP_LALAMOVE
  return p.has_freezer ? '—' : PICKUP_NEEDS_FREEZER
}

const kmText = (km: number): string => (Number.isInteger(km) ? String(km) : String(Math.round(km * 10) / 10))

/** the zone as the page shows it: the affiliate's stored answer (R5, no km) or this visit's place-check (with km) */
export interface ZoneView {
  in_zone: boolean
  km: number | null
  checked_at: string | null
}

/** the stored zone when the affiliate has an answer (R5 `zone.in_zone` true / false); null = none (never checked, the
 *  pin moved since, or an affiliate before R5) */
export function storedZone(p: { zone?: StoredZone | null }): ZoneView | null {
  const z = p.zone
  return z && typeof z.in_zone === 'boolean' ? { in_zone: z.in_zone, km: null, checked_at: z.checked_at ?? null } : null
}

/** this visit's place-check as a ZoneView (checked now) */
export function zoneOfCheck(c: PlaceCheck, at: number): ZoneView {
  const km = c.lalamove.km
  return { in_zone: !!c.lalamove.ok, km: typeof km === 'number' ? km : null, checked_at: new Date(at).toISOString() }
}

/** ask place-check when the page opens? Only a gym of a P5 affiliate (box fields) with a pin and no stored answer:
 *  `zone` absent (an affiliate before R5) or in_zone null (never checked, or the pin moved). A stored answer is shown
 *  as it is; "เช็คอีกครั้ง" asks again. */
export function zoneToAsk(p: {
  type: PartnerType
  box_days?: number[] | null
  gym_lat: number | null
  gym_lng: number | null
  zone?: StoredZone | null
}): boolean {
  if (p.type !== 'gym' || p.box_days === undefined || p.gym_lat === null || p.gym_lng === null) return false
  return !storedZone(p)
}

/** the zone line: "ในโซน Lalamove 4.1 กม." (km from a check made now; the stored answer has none) · out of zone:
 *  "นอกโซน Lalamove ส่งกล่องรวมทาง DHL" while the gym ships (or is about to ship) a box, else "นอกโซน Lalamove" — the
 *  "รับที่ฟิตเนสแบบ" row says what is missing (review I-1 follow-up) */
export function zoneText(z: ZoneView, boxing = false): string {
  if (z.in_zone) return z.km === null ? 'ในโซน Lalamove' : `ในโซน Lalamove ${kmText(z.km)} กม.`
  return boxing ? 'นอกโซน Lalamove ส่งกล่องรวมทาง DHL' : 'นอกโซน Lalamove'
}

/** how far the pin is, for a title ("ห่าง 612.4 กม."); '' without km */
export function zoneKmTitle(z: ZoneView | null): string {
  const km = z?.km
  return typeof km === 'number' ? `ห่าง ${kmText(km)} กม.` : ''
}

const ORDER_MODE_TH: Record<string, string> = { gym: 'รับที่ฟิตเนส', home: 'ส่งบ้าน', express: 'ส่งด่วน', box: 'กล่องรวม DHL' }

/** a shop order's mode in admin copy ("กล่องรวม" is fine here, never in customer copy) */
export function orderModeText(mode: string): string {
  return ORDER_MODE_TH[mode] ?? mode
}

/** what BoxConfig edits: the chosen weekdays (sorted) and the DHL address as typed */
export interface BoxDraft {
  days: number[]
  address: BoxAddress
}

/** the box being edited for a gym: its saved one, else days none and the address read from the gym's address text */
export function boxDraftOf(p: { box_days?: number[] | null; box_address?: BoxAddress | null; gym_address: string | null;
  contact_name: string; phone: string }): BoxDraft {
  return {
    days: [...(p.box_days ?? [])].sort((a, b) => a - b),
    address: p.box_address ? { ...p.box_address } : prefillBoxAddress(p.gym_address, p.contact_name, p.phone),
  }
}

const BOX_KEYS = ['address', 'tambon', 'district', 'province', 'postal_code', 'recipient', 'phone'] as const

/** the draft differs from the saved box (field by field: a JSONB answer may order its keys differently) */
export function boxChanged(d: BoxDraft, saved: { box_days?: number[] | null; box_address?: BoxAddress | null }): boolean {
  const days = [...d.days].sort((a, b) => a - b).join(',')
  if (days !== [...(saved.box_days ?? [])].sort((a, b) => a - b).join(',')) return true
  const a = cleanBoxAddress(d.address)
  const b = saved.box_address
  return !b || BOX_KEYS.some((k) => a[k] !== b[k])
}

export const BOX_FIELD_LABEL: Record<keyof BoxAddress, string> = {
  address: 'ที่อยู่',
  tambon: 'ตำบล',
  district: 'อำเภอ',
  province: 'จังหวัด',
  postal_code: 'รหัสไปรษณีย์',
  recipient: 'ผู้รับ',
  phone: 'เบอร์ผู้รับ',
}

const KW = {
  tambon: /(^|\s)(?:ตำบล|แขวง|ต\.)\s*([^\s,]+)/,
  district: /(^|\s)(?:อำเภอ|เขต|อ\.)\s*([^\s,]+)/,
  province: /(^|\s)(?:จังหวัด|จ\.)\s*([^\s,]+)/,
}

/** the DHL address fields read from the gym's one-line address ("… ตำบล… อำเภอ… จังหวัด… 50200", also "แขวง/เขต", "ต./
 *  อ./จ."; without "จังหวัด" the word before the postal code) — a starting point the owner checks, never sent unseen */
export function prefillBoxAddress(gymAddress: string | null | undefined, recipient: string, phone: string): BoxAddress {
  let rest = (gymAddress ?? '').replace(/\s+/g, ' ').trim()
  const out: BoxAddress = { address: '', tambon: '', district: '', province: '', postal_code: '', recipient, phone }
  const zip = /(?:^|\s)([0-9]{5})\s*$/.exec(rest)
  if (zip) {
    out.postal_code = zip[1]!
    rest = rest.slice(0, zip.index).trim()
  }
  let first = rest.length
  const taken: [number, number][] = []
  for (const k of ['tambon', 'district', 'province'] as const) {
    const m = KW[k].exec(rest)
    if (!m) continue
    out[k] = m[2]!.replace(/,+$/, '')
    const start = m.index + m[1]!.length
    taken.push([start, m.index + m[0].length])
    first = Math.min(first, start)
  }
  if (!out.province) {
    const last = /(\S+)$/.exec(rest)
    if (last && !taken.some(([a, b]) => last.index >= a && last.index < b)) {
      out.province = last[1]!.replace(/,+$/, '')
      if (!taken.length) first = last.index
    }
  }
  out.address = rest.slice(0, first).replace(/[\s,]+$/, '').trim()
  return out
}

const PHONE_SEP = /[\s\-.()]/g

/** the affiliate's normalize_phone: 0[689] + 8 digits or 0[2-7] + 7; +66 / 66 become 0; null when not a Thai number */
export function thaiPhone(raw: string): string | null {
  let s = (raw ?? '').trim().replace(PHONE_SEP, '')
  if (s.startsWith('+66')) s = `0${s.slice(3).replace(/^0/, '')}`
  else if (s.startsWith('66') && (s.length === 10 || s.length === 11)) s = `0${s.slice(2)}`
  return /^0[689][0-9]{8}$/.test(s) || /^0[2-7][0-9]{7}$/.test(s) ? s : null
}

const ASTRAL = /[\u{10000}-\u{10FFFF}]/gu
const cleanText = (v: string): string => (v ?? '').replace(ASTRAL, '').replace(/\s+/g, ' ').trim()

/** what the server stores: text without emoji, one space; the postal code trimmed; the phone normalised when it is one */
export function cleanBoxAddress(a: BoxAddress): BoxAddress {
  return {
    address: cleanText(a.address),
    tambon: cleanText(a.tambon),
    district: cleanText(a.district),
    province: cleanText(a.province),
    postal_code: (a.postal_code ?? '').trim(),
    recipient: cleanText(a.recipient),
    phone: thaiPhone(a.phone) ?? (a.phone ?? '').trim(),
  }
}

/** a Thai postal code: 5 digits whose first two are a province code, 10 (กรุงเทพฯ) … 96 (นราธิวาส) — so 00000 / 99999 /
 *  97xxx never reach DHL (review M3; the province ↔ code match waits for P1's lp_postcode, R5) */
export function thaiPostcode(v: string): boolean {
  if (!/^[0-9]{5}$/.test(v)) return false
  const pre = Number(v.slice(0, 2))
  return pre >= 10 && pre <= 96
}

/** the server's BoxAddressIn rules on the cleaned address: {} = fine, else what is wrong per field (Thai). The street
 *  part may be short (review M4: "12 ม.3" — ตำบล/อำเภอ/จังหวัด are their own fields): 3–250. */
export function boxAddressProblems(a: BoxAddress): Partial<Record<keyof BoxAddress, string>> {
  const c = cleanBoxAddress(a)
  const out: Partial<Record<keyof BoxAddress, string>> = {}
  if (c.address.length < 3 || c.address.length > 250) out.address = 'ที่อยู่ต้องยาว 3–250 ตัวอักษร'
  for (const k of ['tambon', 'district', 'province', 'recipient'] as const) {
    if (!c[k]) out[k] = `กรอก${BOX_FIELD_LABEL[k]}`
    else if (c[k].length > 100) out[k] = `${BOX_FIELD_LABEL[k]}ยาวเกิน 100 ตัวอักษร`
  }
  if (!/^[0-9]{5}$/.test(c.postal_code)) out.postal_code = 'รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก'
  else if (!thaiPostcode(c.postal_code)) out.postal_code = 'ไม่มีรหัสไปรษณีย์นี้ในไทย ตรวจอีกครั้ง'
  if (!thaiPhone(a.phone)) out.phone = 'เบอร์ผู้รับไม่ใช่เบอร์โทรไทย'
  return out
}

// ---------------------------------------------------------------- pin

const PAIR = /(-?\d{1,3}(?:\.\d+)?)\s*[, ]\s*(-?\d{1,3}(?:\.\d+)?)/

/** "13.7,100.5" · "13.7 100.5" · a Google Maps link with "@lat,lng" (or "?q=lat,lng") → the pin; null otherwise */
export function parseLatLng(text: string): { lat: number; lng: number } | null {
  const t = (text ?? '').trim()
  if (!t) return null
  let m: RegExpExecArray | null = null
  if (/^https?:\/\//i.test(t)) {
    m = /@(-?\d{1,3}(?:\.\d+)?),(-?\d{1,3}(?:\.\d+)?)/.exec(t)
    if (!m) {
      const q = /[?&](?:q|query|ll)=(-?\d{1,3}(?:\.\d+)?)(?:,|%2C)(-?\d{1,3}(?:\.\d+)?)/i.exec(t)
      m = q
    }
  } else {
    const whole = new RegExp(`^${PAIR.source}$`).exec(t)
    m = whole
  }
  if (!m) return null
  const lat = Number(m[1])
  const lng = Number(m[2])
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  return { lat, lng }
}

/** a pin inside Thailand's box (lat 5.5–20.5, lng 97.3–105.7): a gym's pin outside it is a typo ("13.7, 10.05") that
 *  P1 refuses (review M8) */
export function inThailand(pin: { lat: number; lng: number }): boolean {
  return pin.lat >= 5.5 && pin.lat <= 20.5 && pin.lng >= 97.3 && pin.lng <= 105.7
}

// ---------------------------------------------------------------- writes

/** A fresh Idempotency-Key (writes get theirs from IdemKeys) */
export function newIdemKey(): string {
  return `aff-${crypto.randomUUID()}`
}

/** a 4xx is the server's answer (it did nothing, or said why); no status (timeout, network) or a 5xx (gateway, the
 *  affiliate down) is no answer: the write may have been done */
function answered(e: unknown): boolean {
  const status = (e as { status?: unknown } | null)?.status
  return typeof status === 'number' && status >= 400 && status < 500
}

/**
 * The Idempotency-Keys of a form's writes, one per scope (a part of the form: 'status', 'slug', 't3', …).
 * A key lives only while its write got no definite answer: the retry of that same body after a timeout, a network
 * error or a 5xx sends the same key, so the affiliate replays it instead of doing it twice. Any 2xx or 4xx ends it,
 * so the next write is a new one even when its body equals an earlier one — otherwise A → B → A would replay the
 * first A and change nothing (review I1). A different body in the same scope replaces a kept key.
 */
/** Why POS (P1) refused a paid shop order, in Thai for the attention card (review I-3); unknown codes as they are.
 *  The code itself stays in the chip's title for whoever asks the developer. */
const HANDOFF_REASON_TH: Record<string, string> = {
  dateUnavailable: 'วันส่งปิดรับแล้ว',
  dateInvalid: 'วันส่งไม่ถูกต้อง',
  customerNotFound: 'ไม่พบลูกค้าใน POS',
  paymentInvalid: 'ข้อมูลการชำระไม่ผ่าน',
  partnerInvalid: 'ข้อมูลร้านไม่ผ่าน',
  discountInvalid: 'ส่วนลดไม่ตรงกับ POS',
  menuClosed: 'มีเมนูปิดขาย',
  itemsInvalid: 'รายการอาหารไม่ถูกต้อง',
  setMenuInvalid: 'แพ็กไม่ถูกต้อง',
  noMeals: 'ไม่มีเมนูอาหาร',
  locationInvalid: 'ที่อยู่ไม่ถูกต้อง',
  locationNeedsFix: 'ที่อยู่ต้องแก้ไข',
  addrIncomplete: 'ที่อยู่ไม่ครบ',
  shipToInvalid: 'จุดรับอาหารไม่ถูกต้อง',
  gymOutOfZone: 'ฟิตเนสอยู่นอกเขตส่ง',
  idemMismatch: 'ข้อมูลไม่ตรงกับที่ส่งครั้งก่อน',
  posUnavailable: 'POS ไม่ตอบ',
  serverError: 'POS ขัดข้อง',
  timeout: 'POS ไม่ตอบ',
}

export function handoffReasonText(code: string | null | undefined): string {
  if (!code) return ''
  return HANDOFF_REASON_TH[code] ?? code
}

export class IdemKeys {
  private readonly kept = new Map<string, { body: string; key: string }>()

  async send<T>(scope: string, body: unknown, call: (key: string) => Promise<T>): Promise<T> {
    const json = JSON.stringify(body ?? null)
    let k = this.kept.get(scope)
    if (!k || k.body !== json) {
      k = { body: json, key: newIdemKey() }
      this.kept.set(scope, k)
    }
    try {
      const out = await call(k.key)
      this.drop(scope, k.key)
      return out
    } catch (e) {
      if (answered(e)) this.drop(scope, k.key)
      throw e
    }
  }

  private drop(scope: string, key: string): void {
    if (this.kept.get(scope)?.key === key) this.kept.delete(scope)
  }
}
